import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Clock, CreditCard, Wallet, Smartphone, X, ShieldCheck } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import PaymentPrivacyDisclosure from '@/components/PaymentPrivacyDisclosure';

const API_BASE = 'https://api.phantompathvpn.com/api';

const StripeCardIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 scale-[1.15] text-white" fill="none" aria-hidden="true">
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3.5 9h17" stroke="currentColor" strokeWidth="2.2" />
    <path d="M6 15h4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PayPalMarkIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 scale-[1.15]" fill="none" aria-hidden="true">
    <path d="M7.2 3.5h6.2c3.4 0 5.2 1.8 4.6 4.6-.6 3-2.8 4.5-6.2 4.5H9.6l-.8 4.1H5.5L8 3.5h-.8Z" fill="#f4fbff" />
    <path d="M10 8.2h5c2.8 0 4.2 1.5 3.7 3.9-.5 2.6-2.5 3.9-5.3 3.9h-1.7l-.8 4.1H7.8L10 8.2Z" fill="#9ddcff" fillOpacity="0.9" />
  </svg>
);

const GoogleMarkIcon = () => (
  <svg viewBox="0 0 18 18" className="h-4 w-4 shrink-0" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.716v2.258h2.909c1.703-1.568 2.684-3.878 2.684-6.614Z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
    <path fill="#FBBC05" d="M3.963 10.706A5.41 5.41 0 0 1 3.681 9c0-.592.102-1.168.282-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.038l3.007-2.332Z" />
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z" />
  </svg>
);

export const PaymentBadges = ({
  methods = ['carrier']
}) => <div className="mt-5 flex w-full flex-col items-center md:min-h-[108px]">
    {methods.length === 1 && methods.includes('carrier') ? <div className="mb-3 flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#008a70]/50 bg-[#d8f6ee] px-3 py-1.5 text-xs text-[#008a70]">
        <Smartphone className="h-4 w-4 scale-[1.15] text-[#008a70]" /> Pay by Mobile
      </div> : <div className="mb-3 flex w-[calc(100%+2rem)] max-w-[10.5rem] flex-col items-center gap-1.5" aria-label="Accepted payment methods">
        {methods.includes('carrier') && <div className="flex h-7 items-center justify-center gap-1.5 rounded-md border border-[#008a70]/50 bg-[#d8f6ee] px-3 text-[11px] text-[#008a70]">
            <Smartphone className="h-3.5 w-3.5 text-[#008a70]" /> Pay by Mobile
          </div>}
        <div className="grid grid-cols-2 gap-1.5">
          {methods.includes('stripe') && <>
              <div className="flex h-7 w-[5rem] items-center justify-center gap-1 rounded-md border border-[#bdd7e8] bg-white/70 px-1 text-[10px] font-semibold text-[#173c5e]" title="Card via Stripe" aria-label="Card via Stripe">
                <StripeCardIcon /> Card
                <span className="sr-only">Card (Stripe)</span>
              </div>
              <div className="flex h-7 w-[5rem] items-center justify-center rounded-md border border-[#bdd7e8] bg-white/70 px-1" title="Apple Pay" aria-label="Apple Pay">
                <img src="/payment-marks/apple-pay.svg" alt="Apple Pay" className="h-[16px] w-auto" />
              </div>
              <div className="flex h-7 w-[5rem] items-center justify-center gap-1 rounded-md border border-[#bdd7e8] bg-white/70 px-1 text-[9px] font-semibold whitespace-nowrap text-[#173c5e]" title="Google Pay" aria-label="Google Pay">
                <GoogleMarkIcon /> Google Pay
              </div>
            </>}
          {methods.includes('paypal') && <div className="flex h-7 w-[5rem] items-center justify-center gap-1 rounded-md border border-[#bdd7e8] bg-white/70 px-1 text-[10px] font-semibold text-[#173c5e]" title="PayPal" aria-label="PayPal">
              <PayPalMarkIcon /> PayPal
            </div>}
        </div>
      </div>}
  </div>;

const plans = [{
  name: "Instant 1-Hour Access",
  price: "£0.20",
  duration: "1 Hour Access",
  desc: "Instant access. Pay only for what you need",
  highlight: false,
  methods: ['carrier'],
  subtitle: "(pay by mobile only)",
  sku: 'vpn-1h',
  dmbRoute: '/dmb-payment_20'
}, {
  name: "24-Hour Pass",
  price: "£1.00",
  duration: "24 Hours Access",
  desc: "Full day access, perfect for trying it out",
  highlight: false,
  methods: ['carrier', 'stripe', 'paypal'],
  sku: 'vpn-24h',
  dmbRoute: '/dmb-payment_100'
}, {
  name: "2-Week Pass",
  price: "£2.00",
  duration: "14 Days Access",
  desc: "Two weeks of flexible access (~14p/day)",
  highlight: true,
  methods: ['carrier', 'stripe', 'paypal'],
  sku: 'vpn-14',
  dmbRoute: '/dmb-payment_200'
}, {
  name: "Month's Pass",
  price: "£3.50",
  duration: "30 Days Access",
  desc: "Thirty days of flexible access (~12p/day)",
  highlight: false,
  methods: ['carrier', 'stripe', 'paypal'],
  sku: 'vpn-30',
  dmbRoute: '/dmb-payment_350'
}];

const Pricing = () => {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleBuy = plan => {
    return; // VPN sales temporarily disabled
    if (plan.methods.length === 1 && plan.methods[0] === 'carrier') {
      window.location.href = `${API_BASE}/checkout/dmb-start?sku=${encodeURIComponent(plan.sku)}`;
    } else {
      setSelectedPlan(plan);
    }
  };

  const checkout = (gateway, plan) => {
    setSelectedPlan(null);
    if (gateway === 'carrier') {
      window.location.href = `${API_BASE}/checkout/dmb-start?sku=${encodeURIComponent(plan.sku)}`;
    } else if (gateway === 'stripe') {
      window.location.href = API_BASE + '/buy/stripe?product=' + plan.sku;
    } else if (gateway === 'paypal') {
      window.location.href = API_BASE + '/buy/paypal?product=' + plan.sku;
    }
  };

  return <section id="pricing" className="scroll-mt-[100px] pt-2 pb-12 px-4 lg:pb-14 bg-[#0a0a0a]">
      <span id="vpn-passes" className="block scroll-mt-[100px]" aria-hidden="true" />
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <span className="text-[#008a70] font-bold tracking-wider text-sm uppercase mb-2 block">Simple Pricing</span>
          <h2 className="mx-auto mb-4 max-w-4xl text-xl font-extrabold leading-tight tracking-tight text-white sm:text-2xl md:text-3xl">VPN Passes</h2>
          <p className="mb-5 mt-2 font-semibold text-[#008a70]">No subscription. No commitment.</p>
          <div className="mx-auto mb-4 max-w-3xl px-1 py-1 text-left">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#008a70]" aria-hidden="true" />
              <div>
                <h3 className="mb-1 text-sm font-semibold text-[#008a70]">VPN access without a subscription</h3>
                <p className="text-sm leading-relaxed text-gray-400">Privacy-conscious services with clear data handling. PhantomPath does not retain VPN browsing activity or traffic content. Read the <a href="/zero-log-manifesto" className="underline decoration-white/25 underline-offset-4 hover:text-white">Technical Manifesto</a> or <a href="/privacy-policy" className="underline decoration-white/25 underline-offset-4 hover:text-white">Privacy Policy</a> for details.</p>
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-lg">No recurring payments. No surprise bills. Just access.</p>
          <p className="mx-auto mt-3 max-w-3xl text-xs leading-relaxed text-gray-400 sm:text-sm">
            Card, Apple Pay and Google Pay via Stripe <span aria-hidden="true">•</span> PayPal <span aria-hidden="true">•</span> UK mobile billing on eligible VPN passes <span aria-hidden="true">•</span>{' '}
            <a href="/refund-policy" className="text-gray-300 underline decoration-white/25 underline-offset-4 transition-colors hover:text-white">Refund Policy</a>
          </p>
        </div>

        <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 gap-0 md:gap-8 lg:gap-6 items-stretch">
          {plans.map((plan, index) => <div id={['vpn-1-hour', 'vpn-24-hour', 'vpn-2-week', 'vpn-30-day'][index]} key={plan.name} className={`scroll-mt-[150px] relative flex flex-col p-6 lg:p-8 rounded-3xl border transition-colors duration-150 group mb-8 md:mb-0 h-full ${plan.highlight ? 'public-featured-product-card bg-[#141515] border-[#008a70]/25 z-10 hover:border-[#008a70]/40' : 'bg-[#111] border-white/[0.07] hover:border-white/15 hover:bg-[#141414]'}`}>
              <div className="mb-6 shrink-0">
                <h3 className={`mb-0 text-xl font-bold md:min-h-[3rem] md:text-lg ${plan.highlight ? 'text-[#008a70]' : 'text-white'}`}>
                  {plan.name}
                </h3>
                {plan.subtitle && <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight mb-2">
                    {plan.subtitle}
                  </p>}
                <div className="flex items-baseline gap-1 mb-2 mt-2">
                  <span className="text-[2.5rem] font-extrabold text-white">{plan.price}</span>
                </div>
                <p className="text-base font-bold text-[#008a70] mt-1">{plan.duration}</p>
              </div>

              <div className="flex-grow mb-8">
                <p className="text-sm text-gray-300 font-medium mb-4 pb-4 border-b border-white/10">{plan.desc}</p>
                <ul className="space-y-3">
                   <li className="flex items-center gap-3 text-sm text-gray-300">
                     <Check className={`w-4 h-4 ${plan.highlight ? 'text-[#008a70]' : 'text-gray-500'}`} />
                     <span>Instant Access</span>
                   </li>
                   <li className="flex items-center gap-3 text-sm text-gray-300">
                     <Clock className={`w-4 h-4 ${plan.highlight ? 'text-[#008a70]' : 'text-gray-500'}`} />
                     <span>No recurring fees</span>
                   </li>
                </ul>
              </div>

              <div className="mt-auto shrink-0 flex flex-col items-center">
                <Button data-ga-event="begin_checkout" data-funnel-stage="product_purchase_clicked" data-product-name={plan.name} data-product-id={plan.sku} data-value={plan.price} data-currency="GBP" onClick={() => {}} disabled aria-disabled="true" className="w-full font-bold rounded-xl h-12 uppercase tracking-wide text-[#68b8ff] hover:text-white bg-transparent border-2 border-[#68b8ff] hover:bg-[#68b8ff] transition-all duration-300 scale-100 hover:scale-105 glow-phantom glow-phantom-hover mt-auto">
                  Get Instant Access
                </Button>
                <PaymentBadges methods={plan.methods} />
              </div>
            </div>)}
        </div>

        <div className="mx-auto mt-6 max-w-4xl">
          <PaymentPrivacyDisclosure>
            <p>18+ ONLY. VPN access begins after successful payment. Cancellation and refund rights depend on the applicable law and service status; see the Refund Policy. Standard network rates may apply. Payment and service operations may be monitored for fraud, abuse, infrastructure security and legal compliance. PhantomPath does not monitor VPN browsing activity or traffic content. VPN service is intended for lawful privacy and security purposes. Users remain responsible for complying with applicable laws and service terms.</p>
          </PaymentPrivacyDisclosure>
        </div>
      </div>

      <AnimatePresence>
        {selectedPlan && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="public-checkout-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelectedPlan(null)}>
            <motion.div data-ga-location="vpn_payment_dialog" data-product-id={selectedPlan.sku} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="public-checkout-dialog bg-[#111] border border-[#008a70]/20 rounded-2xl w-full max-w-sm shadow-[0_0_60px_rgba(0,138,112,0.1)] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <div>
                  <h3 className="text-white font-bold text-base">{selectedPlan.name}</h3>
                  <p className="text-[#008a70] text-2xl font-bold mt-1">{selectedPlan.price}</p>
                  <p className="text-[#008a70] text-sm font-bold">{selectedPlan.duration}</p>
                </div>
                <button onClick={() => setSelectedPlan(null)} className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center">
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-gray-400 text-xs mb-4">Choose your payment method:</p>
                {selectedPlan.methods.includes('carrier') && <button data-ga-event="add_payment_info" data-product-name={selectedPlan.name} data-product-id={selectedPlan.sku} data-value={selectedPlan.price} data-currency="GBP" data-payment-method="carrier_billing" onClick={() => checkout('carrier', selectedPlan)} className="w-full h-12 bg-[#008a70]/10 border border-[#008a70]/40 text-[#008a70] font-bold text-sm rounded-xl hover:bg-[#008a70]/20 transition-all flex items-center justify-center gap-2">
                    <Smartphone className="w-4 h-4" /> Pay via Phone Bill (UK Only)
                  </button>}
                {selectedPlan.methods.includes('stripe') && <button data-ga-event="add_payment_info" data-product-name={selectedPlan.name} data-product-id={selectedPlan.sku} data-value={selectedPlan.price} data-currency="GBP" data-payment-method="stripe" onClick={() => checkout('stripe', selectedPlan)} className="w-full h-12 bg-white/5 border border-white/10 text-white font-bold text-sm rounded-xl hover:bg-white/10 transition-all flex items-center justify-center gap-2">
                    <CreditCard className="w-4 h-4" /> Pay with Card (Stripe)
                  </button>}
                {selectedPlan.methods.includes('paypal') && <button data-ga-event="add_payment_info" data-product-name={selectedPlan.name} data-product-id={selectedPlan.sku} data-value={selectedPlan.price} data-currency="GBP" data-payment-method="paypal" onClick={() => checkout('paypal', selectedPlan)} className="w-full h-12 bg-[#FFE600]/5 border border-[#FFE600]/20 text-[#FFE600] font-bold text-sm rounded-xl hover:bg-[#FFE600]/10 transition-all flex items-center justify-center gap-2">
                    <Wallet className="w-4 h-4" /> Pay with PayPal
                  </button>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
};
export default Pricing;
